"use client"

// src/components/pages/MenuOrderPage.jsx
import { useState, useCallback } from "react"
import { useParams } from "react-router-dom"
import DashboardTemplate from "../templates/DashboardTemplate"
import Card from "../molecules/Card"
import Button from "../atoms/Button"
import useFetch from "../../hooks/useFetch"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"
import Input from "../atoms/Input"

import { loadStripe } from "@stripe/stripe-js"
import { Elements, useStripe, useElements, CardElement } from "@stripe/react-stripe-js"

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)

const Spinner = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
  </div>
)

// Modal to pick quantity/note
const OrderModal = ({ item, onClose, onConfirm }) => {
  const [quantity, setQuantity] = useState(1)
  const [note, setNote] = useState("")

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4 pb-2 border-b">
          <h2 className="text-xl font-bold text-gray-800">Order: {item.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-full p-1 transition-colors duration-200"
            aria-label="Close Modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <p className="mb-4 text-gray-600">{item.description}</p>

        <div className="mb-4">
          <label htmlFor="quantity" className="block mb-2 font-medium text-gray-700">
            Quantity:
          </label>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-l-lg hover:bg-gray-300 transition-colors duration-200"
              aria-label="Decrease quantity"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4"></path>
              </svg>
            </button>
            <input
              id="quantity"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="w-16 py-2 px-3 text-center border-y focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <button
              type="button"
              onClick={() => setQuantity(quantity + 1)}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-r-lg hover:bg-gray-300 transition-colors duration-200"
              aria-label="Increase quantity"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
              </svg>
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="note" className="block mb-2 font-medium text-gray-700">
            Special Instructions (optional):
          </label>
          <textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
            rows="3"
            placeholder="Any special requests or allergies?"
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button onClick={onClose} className="bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500">
            Cancel
          </Button>
          <Button onClick={() => onConfirm({ item, quantity, note })}>
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              ></path>
            </svg>
            Add to Order
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function MenuOrderPage() {
  const { restaurantId } = useParams()
  const { user } = useAuth()
  const { data: menus, loading, error } = useFetch(`/menus/restaurant/${restaurantId}`)
  const { data: restaurant } = useFetch(`/restaurants/${restaurantId}`)

  const [orderCart, setOrderCart] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [notification, setNotification] = useState(null)

  const [orderType, setOrderType] = useState("dine-in")
  const [tableNumber, setTableNumber] = useState("")
  const [deliveryAddress, setDeliveryAddress] = useState("")
  const [deliveryCoordinates, setDeliveryCoordinates] = useState(null)

  const [paymentMethod, setPaymentMethod] = useState("cash")

  const showNotification = (type, msg) => {
    setNotification({ type, msg })
    setTimeout(() => setNotification(null), 5000)
  }

  const totalAmount = useCallback(
    () => orderCart.reduce((sum, o) => sum + o.item.price * o.quantity, 0) * 100,
    [orderCart],
  )

  const addToCart = (detail) => {
    setOrderCart([...orderCart, detail])
    showNotification("success", `${detail.item.name} added to your order`)
  }

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      return showNotification("error", "Geolocation is not supported by your browser")
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setDeliveryCoordinates({
          lat: coords.latitude,
          lng: coords.longitude,
        })
        showNotification("success", "Your location has been set")
      },
      () => showNotification("error", "Failed to get your location"),
    )
  }

  const removeFromCart = (index) => {
    const newCart = [...orderCart]
    newCart.splice(index, 1)
    setOrderCart(newCart)
    showNotification("info", "Item removed from your order")
  }

  // post order to backend
  const placeOrder = async ({ paymentIntentId, paymentStatus }) => {
    if (!user) return showNotification("error", "Please login to place an order")
    if (!orderCart.length) return showNotification("error", "Your cart is empty")
    if (orderType === "dine-in" && !tableNumber)
      return showNotification("error", "Table number is required for dine-in orders")
    if (orderType === "delivery" && !deliveryAddress.trim() && !deliveryCoordinates)
      return showNotification("error", "Delivery address or location is required")

    const payload = {
      user: user._id,
      restaurant: restaurantId,
      orderType,
      orderItems: orderCart.map((o) => ({
        item: o.item._id,
        quantity: o.quantity,
        note: o.note,
      })),
      paymentMethod,
      paymentStatus: paymentMethod === "card" ? paymentStatus : "pending",
      ...(paymentMethod === "card" ? { paymentIntentId } : {}),
    }
    if (orderType === "dine-in") payload.tableNumber = tableNumber
    if (orderType === "delivery") {
      if (deliveryAddress) payload.deliveryAddress = deliveryAddress
      if (deliveryCoordinates) payload.deliveryCoordinates = deliveryCoordinates
    }

    try {
      await api.post("/orders", payload)
      showNotification("success", "Order placed successfully! You can track it in 'My Orders'")
      setOrderCart([])
      setTableNumber("")
      setDeliveryAddress("")
      setDeliveryCoordinates(null)
      setPaymentMethod("cash")
    } catch (err) {
      console.error(err)
      showNotification("error", err.response?.data?.error || "Failed to place order")
    }
  }

  // inside <Elements> we use these hooks
  const CheckoutForm = () => {
    const stripe = useStripe()
    const elements = useElements()
    const [busy, setBusy] = useState(false)

    const handleSubmit = async (e) => {
      e.preventDefault()
      if (!stripe || !elements) return
      setBusy(true)
      try {
        // 1) get clientSecret
        const { data } = await api.post("/stripe/create-payment-intent", { amount: totalAmount() })
        // 2) confirm in-app
        const res = await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        })
        if (res.error) {
          showNotification("error", res.error.message)
        } else if (res.paymentIntent.status === "succeeded") {
          await placeOrder({
            paymentIntentId: res.paymentIntent.id,
            paymentStatus: "succeeded",
          })
        }
      } catch (err) {
        console.error(err)
        showNotification("error", "Payment failed")
      }
      setBusy(false)
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 border rounded-lg bg-white">
          <CardElement options={{ style: { base: { fontSize: "16px" } } }} />
        </div>
        <Button type="submit" disabled={!stripe || busy} className="w-full">
          {busy ? (
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              Processing...
            </div>
          ) : (
            `Pay & Place Order ($${(totalAmount() / 100).toFixed(2)})`
          )}
        </Button>
      </form>
    )
  }

  return (
    <DashboardTemplate>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Menu & Order</h1>
            {restaurant && <p className="text-gray-600">{restaurant.name}</p>}
          </div>
        </div>

        {notification && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              notification.type === "error"
                ? "bg-red-100 text-red-700 border-l-4 border-red-500"
                : notification.type === "success"
                  ? "bg-green-100 text-green-700 border-l-4 border-green-500"
                  : "bg-blue-100 text-blue-700 border-l-4 border-blue-500"
            }`}
          >
            {notification.msg}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Menu Items Section */}
          <div className="lg:col-span-2">
            {/* Order Type Selection */}
            <div className="mb-6 p-5 bg-white rounded-lg shadow-md border border-gray-100">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Select Order Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div
                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                    orderType === "dine-in"
                      ? "bg-amber-50 border-amber-500 shadow-sm"
                      : "bg-white border-gray-200 hover:border-amber-300"
                  }`}
                  onClick={() => setOrderType("dine-in")}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        orderType === "dine-in" ? "border-amber-500" : "border-gray-300"
                      }`}
                    >
                      {orderType === "dine-in" && <div className="w-3 h-3 rounded-full bg-amber-500"></div>}
                    </div>
                    <div>
                      <span className="font-medium text-gray-800">Dine-In</span>
                      <p className="text-xs text-gray-500 mt-1">Eat at the restaurant</p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                    orderType === "pickup"
                      ? "bg-amber-50 border-amber-500 shadow-sm"
                      : "bg-white border-gray-200 hover:border-amber-300"
                  }`}
                  onClick={() => setOrderType("pickup")}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        orderType === "pickup" ? "border-amber-500" : "border-gray-300"
                      }`}
                    >
                      {orderType === "pickup" && <div className="w-3 h-3 rounded-full bg-amber-500"></div>}
                    </div>
                    <div>
                      <span className="font-medium text-gray-800">Pickup</span>
                      <p className="text-xs text-gray-500 mt-1">Collect your order</p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                    orderType === "delivery"
                      ? "bg-amber-50 border-amber-500 shadow-sm"
                      : "bg-white border-gray-200 hover:border-amber-300"
                  }`}
                  onClick={() => setOrderType("delivery")}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        orderType === "delivery" ? "border-amber-500" : "border-gray-300"
                      }`}
                    >
                      {orderType === "delivery" && <div className="w-3 h-3 rounded-full bg-amber-500"></div>}
                    </div>
                    <div>
                      <span className="font-medium text-gray-800">Delivery</span>
                      <p className="text-xs text-gray-500 mt-1">Delivered to your address</p>
                    </div>
                  </div>
                </div>
              </div>

              {orderType === "dine-in" && (
                <div className="mt-4">
                  <label htmlFor="tableNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Table Number:
                  </label>
                  <Input
                    id="tableNumber"
                    type="text"
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                    placeholder="Enter your table number"
                  />
                </div>
              )}

              {orderType === "delivery" && (
                <div className="mt-4 space-y-3">
                  <div>
                    <label htmlFor="deliveryAddress" className="block text-sm font-medium text-gray-700 mb-1">
                      Delivery Address:
                    </label>
                    <Input
                      id="deliveryAddress"
                      type="text"
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder="Enter your delivery address"
                    />
                  </div>
                  <div>
                    <Button onClick={handleUseMyLocation} className="bg-amber-100 text-amber-800 hover:bg-amber-200">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        ></path>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        ></path>
                      </svg>
                      Use My Location
                    </Button>
                    {deliveryCoordinates && (
                      <p className="text-sm text-green-600 mt-2">
                        <svg
                          className="w-4 h-4 inline-block mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Location set successfully
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">Error: {error.message}</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Display Menu Items */
              <div>
                {/* Menu */}
                {menus && menus.length > 0 && menus[0].categories && menus[0].categories.length > 0 ? (
                  menus[0].categories.map((cat, i) => (
                    <div key={i} className="mb-8">
                      <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b pb-2">{cat.header}</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {cat.items &&
                          cat.items.map((item, j) => (
                            <Card
                              key={j}
                              header={<h3 className="text-lg font-semibold text-gray-800">{item.name}</h3>}
                              footer={
                                <div className="flex justify-between items-center">
                                  <span className="text-lg font-bold text-amber-600">${item.price.toFixed(2)}</span>
                                  <Button onClick={() => setSelectedItem(item)}>
                                    <svg
                                      className="w-5 h-5 mr-2"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                      ></path>
                                    </svg>
                                    Add to Order
                                  </Button>
                                </div>
                              }
                              className="hover:shadow-md transition-shadow duration-300"
                            >
                              <p className="text-gray-600 mb-3">{item.description}</p>
                              {item.imageUrl ? (
                                <img
                                  src={item.imageUrl || "/placeholder.svg"}
                                  alt={item.name}
                                  className="w-full h-48 object-cover rounded-md mb-3"
                                />
                              ) : (
                                <div className="h-32 bg-gray-100 flex items-center justify-center text-gray-500 rounded-md mb-3">
                                  No Image
                                </div>
                              )}
                            </Card>
                          ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <svg
                      className="w-16 h-16 text-gray-400 mx-auto mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      ></path>
                    </svg>
                    <p className="text-gray-500 text-lg">No menu items available</p>
                    <p className="text-gray-400 mt-2">The restaurant hasn't added any menu items yet</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Summary Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md border border-gray-100 sticky top-6">
              <div className="p-5 border-b">
                <h2 className="text-xl font-bold text-gray-800">Your Order</h2>
              </div>

              <div className="p-5">
                {orderCart.length === 0 ? (
                  <div className="text-center py-8">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                      ></path>
                    </svg>
                    <p className="text-gray-500">Your cart is empty</p>
                    <p className="text-gray-400 text-sm mt-1">Add items from the menu to start your order</p>
                  </div>
                ) : (
                  <div>
                    <ul className="divide-y divide-gray-100">
                      {orderCart.map((order, index) => (
                        <li key={index} className="py-3">
                          <div className="flex justify-between">
                            <div>
                              <div className="flex items-center">
                                <span className="font-medium text-gray-800">{order.item.name}</span>
                                <span className="ml-2 bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full">
                                  x{order.quantity}
                                </span>
                              </div>
                              {order.note && <p className="text-sm text-gray-500 italic mt-1">{order.note}</p>}
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium text-gray-800 mr-3">
                                ${(order.item.price * order.quantity).toFixed(2)}
                              </span>
                              <button
                                onClick={() => removeFromCart(index)}
                                className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                                aria-label="Remove item"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  ></path>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <div className="border-t border-gray-100 mt-4 pt-4">
                      <div className="flex justify-between items-center font-bold text-lg text-gray-800">
                        <span>Total:</span>
                        <span>${(totalAmount() / 100).toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method:</label>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div
                          className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                            paymentMethod === "cash"
                              ? "bg-amber-50 border-amber-500 shadow-sm"
                              : "bg-white border-gray-200 hover:border-amber-300"
                          }`}
                          onClick={() => setPaymentMethod("cash")}
                        >
                          <div className="flex items-center">
                            <div
                              className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${
                                paymentMethod === "cash" ? "border-amber-500" : "border-gray-300"
                              }`}
                            >
                              {paymentMethod === "cash" && <div className="w-2 h-2 rounded-full bg-amber-500"></div>}
                            </div>
                            <span className="font-medium text-gray-800">Cash</span>
                          </div>
                        </div>

                        <div
                          className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                            paymentMethod === "card"
                              ? "bg-amber-50 border-amber-500 shadow-sm"
                              : "bg-white border-gray-200 hover:border-amber-300"
                          }`}
                          onClick={() => setPaymentMethod("card")}
                        >
                          <div className="flex items-center">
                            <div
                              className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${
                                paymentMethod === "card" ? "border-amber-500" : "border-gray-300"
                              }`}
                            >
                              {paymentMethod === "card" && <div className="w-2 h-2 rounded-full bg-amber-500"></div>}
                            </div>
                            <span className="font-medium text-gray-800">Card</span>
                          </div>
                        </div>
                      </div>

                      {paymentMethod === "card" ? (
                        <Elements stripe={stripePromise}>
                          <CheckoutForm />
                        </Elements>
                      ) : (
                        <Button onClick={() => placeOrder({})} className="w-full">
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                          Place Order
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedItem && <OrderModal item={selectedItem} onClose={() => setSelectedItem(null)} onConfirm={addToCart} />}
    </DashboardTemplate>
  )
}
